import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MessageCircle, RefreshCw, Save, Settings2 } from "lucide-react";
import { toast } from "react-hot-toast";

import useClientsConfig from "../../Services/ClientServices/ClientConfig/useClientConfig";
import useOrderSettings from "../../Services/OrderServices/OrderSettingsServices/useOrderSettings";
import ConfigFormCard from "./ClientConfigParts/ConfigFormCard";
import GstDetailsCard from "./ClientConfigParts/GstDetailsCard";
import GstEditModal from "./ClientConfigParts/GstEditModal";
import OrganizationImageCard from "./ClientConfigParts/OrganizationImageCard";
import OrganizationImageModal from "./ClientConfigParts/OrganizationImageModal";

import {
  BORDER_SMART,
  THEME_BLUE_BG,
  getInitialFormData,
  getInitialGstForm,
  mapRestaurantConfigToForm,
  toNumber,
} from "./ClientConfigParts/configHelpers";
import { uploadItemImage } from "../../../Utils/imageUploadUtils";

import {
  ConfigSkeleton,
  SyncPill,
} from "./ClientConfigParts/ClientConfigShared";
import { GlobalContext } from "../../../Context/GlobalContext";

const normalizeTimeForInput = (value = "") => {
  const time = String(value || "").trim();

  if (!time) return "";

  // HTML <input type="time"> expects HH:mm, while API returns HH:mm:ss.
  const match = time.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "";
};

const normalizeTimeForApi = (value = "") => {
  const time = normalizeTimeForInput(value);
  return time ? `${time}:00` : "";
};

const normalizeOrganizationDetails = (data = {}, fallback = {}) => ({
  organizationName:
    data?.organizationName ||
    data?.organization_name ||
    fallback?.organizationName ||
    "",
  gst: data?.gst || data?.gst_no || fallback?.gst || "",
  pan_no: data?.pan_no || data?.panNo || "",
  brand_image:
    data?.brand_image ||
    data?.brandImage ||
    data?.image_url ||
    fallback?.brand_image ||
    "",
  pament_gateway:
    data?.pament_gateway || data?.payment_gateway || data?.paymentGateway || "",
  opening_time: normalizeTimeForInput(
    data?.opening_time || data?.openingTime || "",
  ),
  closing_time: normalizeTimeForInput(
    data?.closing_time || data?.closingTime || "",
  ),
});

export default function ClientConfigTab({
  clientId,
  clientName,
  selectedClient = null,
}) {
  const { anlookApi } = useContext(GlobalContext);
  const {
    saveClientConfiguration,
    fetchRestaurantOrderDetails,
    getGSTDetails,
    updateGSTDetails,
    getClientCustomMessage,
    insertClientCustomMessage,
    busy,
  } = useClientsConfig();

  const { getOrganizationDetails, editOrganizationInfo } = useOrderSettings();

  const existingRestaurantGstRef = useRef("");
  const hasFetchedForClientRef = useRef("");

  const apiRef = useRef({
    fetchRestaurantOrderDetails,
    getGSTDetails,
    updateGSTDetails,
    saveClientConfiguration,
    getOrganizationDetails,
    editOrganizationInfo,
    getClientCustomMessage,
    insertClientCustomMessage,
  });

  useEffect(() => {
    apiRef.current = {
      fetchRestaurantOrderDetails,
      getGSTDetails,
      updateGSTDetails,
      saveClientConfiguration,
      getOrganizationDetails,
      editOrganizationInfo,
      getClientCustomMessage,
      insertClientCustomMessage,
    };
  }, [
    fetchRestaurantOrderDetails,
    getGSTDetails,
    updateGSTDetails,
    saveClientConfiguration,
    getOrganizationDetails,
    editOrganizationInfo,
    getClientCustomMessage,
    insertClientCustomMessage,
  ]);

  const safeClientId = useMemo(() => {
    return String(clientId || selectedClient?.us_code || "").trim();
  }, [clientId, selectedClient?.us_code]);

  const client = useMemo(() => {
    if (!safeClientId) return null;

    return {
      ...(selectedClient || {}),
      us_code: safeClientId,
      organization_name: selectedClient?.organization_name || clientName || "",
    };
  }, [safeClientId, selectedClient, clientName]);

  const adminPkId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("Admin") || "{}")?.pkid;
    } catch {
      return "";
    }
  }, []);

  const [formData, setFormData] = useState(() =>
    getInitialFormData({ restaurantName: clientName || "" }),
  );

  const [gstForm, setGstForm] = useState(() => getInitialGstForm());

  const [initialLoading, setInitialLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [savingGst, setSavingGst] = useState(false);
  const [gstModalOpen, setGstModalOpen] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const [organization, setOrganization] = useState(null);
  const [organizationForm, setOrganizationForm] = useState(() =>
    normalizeOrganizationDetails(),
  );
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingOrganization, setSavingOrganization] = useState(false);

  const DEFAULT_CHATBOT_MESSAGE =
    "Hi {0}, 👋\n" +
    "🌟 Welcome to {1} 🌟\n" +
    "🍔 Ab 200 ka khaana, 300 mei kyu lena?.\n" +
    "With us, you always get lower prices than Swiggy & Zomato 🚀.\n" +
    "🎉 Enjoy *FREE Home Delivery* on all orders above 100. 🛵✨\n" +
    "📲 Order now through our WhatsApp Bot and save more!\n" +
    "{1} Team";

  const [chatbotMessage, setChatbotMessage] = useState(DEFAULT_CHATBOT_MESSAGE);
  const [chatbotMessageLoading, setChatbotMessageLoading] = useState(false);

  const halfTax = useMemo(() => {
    return toNumber(gstForm.taxGst) / 2;
  }, [gstForm.taxGst]);

  const isSavingConfig = busy?.[client?.us_code] === "config";
  const isSavingGst = savingGst || busy?.[client?.us_code] === "gst";

  const updateForm = useCallback((key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    if (!client?.us_code) return;

    const clientKey = `${client.us_code}_${adminPkId || "3"}`;

    if (hasFetchedForClientRef.current === clientKey) {
      return;
    }

    hasFetchedForClientRef.current = clientKey;

    let ignore = false;

    const loadAllDetails = async () => {
      if (!loadedOnce) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [restaurantRows, gstData, organizationRes, customMessage] =
          await Promise.all([
            apiRef.current.fetchRestaurantOrderDetails({
              adminId: adminPkId || "3",
              userid: client.us_code,
            }),
            apiRef.current.getGSTDetails(client.us_code),
            apiRef.current.getOrganizationDetails(client.us_code),
            apiRef.current.getClientCustomMessage(client.us_code),
          ]);

        if (ignore) return;

        const safeRestaurantRows = Array.isArray(restaurantRows)
          ? restaurantRows
          : [];

        const restaurantInfo = safeRestaurantRows[0] || null;

        const mappedForm = mapRestaurantConfigToForm(
          safeRestaurantRows,
          getInitialFormData({
            restaurantName: client.organization_name || clientName || "",
          }),
        );

        const nextGst = gstData?.gst || "";
        const nextGstType = gstData?.gst_type || "incl";
        const nextTaxGst =
          gstData?.tax_gst !== null && gstData?.tax_gst !== undefined
            ? String(gstData.tax_gst)
            : "";

        const protectedGst = nextGst || mappedForm.restaurantgst || "";

        existingRestaurantGstRef.current = protectedGst;

        setFormData({
          ...mappedForm,
          restaurantName:
            mappedForm.restaurantName ||
            restaurantInfo?.restaurant_name ||
            client.organization_name ||
            clientName ||
            "",
          restaurantgst: protectedGst,
        });

        setGstForm({
          gst: protectedGst,
          gstType: nextGstType,
          taxGst: nextTaxGst,
        });

        const normalizedOrganization = normalizeOrganizationDetails(
          organizationRes?.data || {},
          {
            organizationName:
              restaurantInfo?.restaurant_name ||
              client.organization_name ||
              clientName ||
              "",
            gst: protectedGst,
            brand_image:
              restaurantInfo?.brand_image || restaurantInfo?.image_url || "",
          },
        );

        setOrganization({
          ...(organizationRes?.data || {}),
          ...normalizedOrganization,
          organization_name: normalizedOrganization.organizationName,
        });
        setOrganizationForm(normalizedOrganization);

        setChatbotMessage(customMessage || DEFAULT_CHATBOT_MESSAGE);
        setPreview(null);
        setLoadedOnce(true);
      } catch (error) {
        console.error("Client config load failed:", error);
        toast.error("Failed to load client configuration");

        if (!ignore) {
          setLoadedOnce(true);
        }
      } finally {
        if (!ignore) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadAllDetails();

    return () => {
      ignore = true;
    };
  }, [
    client?.us_code,
    client?.organization_name,
    clientName,
    adminPkId,
    loadedOnce,
  ]);

  useEffect(() => {
    return () => {
      hasFetchedForClientRef.current = "";
    };
  }, [client?.us_code]);

  const handleOrganizationFieldChange = useCallback((key, value) => {
    setOrganizationForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!client?.us_code) {
      toast.error("Client id missing");
      event.target.value = "";
      return;
    }

    try {
      setUploadingImage(true);

      const localPreview = URL.createObjectURL(file);
      setPreview(localPreview);

      const uploadResult = await uploadItemImage({
        anlookApi,
        userid: client.us_code,
        itemName: "organization_image",
        file,
        maxSizeMB: 5,
      });

      const imageUrl = uploadResult?.imageUrl;

      if (!imageUrl) {
        throw new Error("Image URL missing");
      }

      setPreview(imageUrl);
      setOrganizationForm((prev) => ({
        ...prev,
        brand_image: imageUrl,
      }));
      setOrganization((prev) => ({
        ...(prev || {}),
        brand_image: imageUrl,
      }));

      toast.success("Image uploaded. Save organization details to sync it.");
    } catch (error) {
      console.error("handleImageSelect failed:", error);
      setPreview(null);
      toast.error(error?.message || "Failed to upload organization image");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleOrganizationSave = async () => {
    if (!client?.us_code) {
      toast.error("Client id missing");
      return;
    }

    if (!organizationForm.organizationName?.trim()) {
      toast.error("Please enter organization name");
      return;
    }

    const payload = {
      userid: client.us_code,
      organizationName: organizationForm.organizationName.trim(),
      gst:
        organizationForm.gst?.trim() ||
        gstForm.gst?.trim() ||
        existingRestaurantGstRef.current?.trim() ||
        "",
      pan_no: organizationForm.pan_no?.trim() || "",
      brand_image: organizationForm.brand_image?.trim() || "",
      pament_gateway: organizationForm.pament_gateway?.trim() || "",
      opening_time: normalizeTimeForApi(organizationForm.opening_time),
      closing_time: normalizeTimeForApi(organizationForm.closing_time),
    };

    try {
      setSavingOrganization(true);

      const result = await apiRef.current.editOrganizationInfo(payload);

      if (!result?.success) {
        throw new Error(result?.message || "Organization update failed");
      }

      const nextOrganization = {
        ...(organization || {}),
        ...payload,
        organization_name: payload.organizationName,
        opening_time: normalizeTimeForInput(payload.opening_time),
        closing_time: normalizeTimeForInput(payload.closing_time),
      };

      setOrganization(nextOrganization);
      setOrganizationForm((prev) => ({
        ...prev,
        ...payload,
        opening_time: normalizeTimeForInput(payload.opening_time),
        closing_time: normalizeTimeForInput(payload.closing_time),
      }));
      setFormData((prev) => ({
        ...prev,
        restaurantName: payload.organizationName,
        restaurantgst: payload.gst || prev.restaurantgst,
      }));

      if (payload.gst) {
        existingRestaurantGstRef.current = payload.gst;
        setGstForm((prev) => ({ ...prev, gst: payload.gst }));
      }

      setPreview(null);
      setImageModalOpen(false);
      toast.success(result?.message || "Organization updated successfully");
    } catch (error) {
      console.error("handleOrganizationSave failed:", error);
      toast.error(error?.message || "Failed to update organization");
    } finally {
      setSavingOrganization(false);
    }
  };

  // const handleSave = async () => {
  //   if (!client?.us_code) {
  //     toast.error("Client id missing");
  //     return;
  //   }

  //   const protectedRestaurantGst =
  //     gstForm.gst?.trim() ||
  //     formData.restaurantgst?.trim() ||
  //     existingRestaurantGstRef.current?.trim() ||
  //     "";

  //   const payload = {
  //     restaurantgst: protectedRestaurantGst,
  //     restaurantName: formData.restaurantName,
  //     takeaway: formData.takeaway ? "Y" : "N",
  //     homeDelivery: formData.homeDelivery ? "Y" : "N",
  //     dining: formData.dining ? "Y" : "N",
  //     carOrder: formData.carOrder ? "Y" : "N",
  //     roomOrder: formData.roomOrder ? "Y" : "N",
  //     posOrder: formData.posOrder ? "Y" : "N",
  //     restaurantAddress: formData.restaurantAddress,
  //     restaurantPincode: formData.restaurantPincode,
  //     settlementAccount: formData.settlementAccount,
  //     settlementAccountifsc: formData.settlementAccountifsc,
  //   };

  //   await apiRef.current.saveClientConfiguration({
  //     adminId: adminPkId || "3",
  //     userid: client.us_code,
  //     payload,
  //   });
  // };

  const handleSave = async () => {
    if (!client?.us_code) {
      toast.error("Client id missing");
      return;
    }

    const mobileNumber = String(formData.mobileNumber || "")
      .replace(/\D/g, "")
      .trim();

    if (!mobileNumber) {
      toast.error("Please enter mobile number");
      return;
    }

    if (mobileNumber.length !== 10) {
      toast.error("Please enter valid 10 digit mobile number");
      return;
    }

    const protectedRestaurantGst =
      gstForm.gst?.trim() ||
      formData.restaurantgst?.trim() ||
      existingRestaurantGstRef.current?.trim() ||
      "";

    const payload = {
      restaurantgst: protectedRestaurantGst,
      restaurantName: formData.restaurantName,
      takeaway: formData.takeaway ? "Y" : "N",
      homeDelivery: formData.homeDelivery ? "Y" : "N",
      dining: formData.dining ? "Y" : "N",
      carOrder: formData.carOrder ? "Y" : "N",
      roomOrder: formData.roomOrder ? "Y" : "N",
      posOrder: formData.posOrder ? "Y" : "N",
      restaurantAddress: formData.restaurantAddress,
      restaurantPincode: formData.restaurantPincode,
      settlementAccount: formData.settlementAccount,
      settlementAccountifsc: formData.settlementAccountifsc,
    };

    await apiRef.current.saveClientConfiguration({
      mobileNumber,
      userid: client.us_code,
      payload,
    });
  };

  const handleGstUpdate = async () => {
    if (!client?.us_code) {
      toast.error("Client id missing");
      return;
    }

    if (!gstForm.gst?.trim()) {
      toast.error("Please enter GST number");
      return;
    }

    if (!gstForm.taxGst?.trim()) {
      toast.error("Please enter total GST percentage");
      return;
    }

    if (Number.isNaN(Number(gstForm.taxGst)) || Number(gstForm.taxGst) < 0) {
      toast.error("Please enter valid GST percentage");
      return;
    }

    setSavingGst(true);

    try {
      const updatedGst = gstForm.gst.trim().toUpperCase();

      await apiRef.current.updateGSTDetails({
        userid: client.us_code,
        gst: updatedGst,
        gstType: gstForm.gstType,
        taxGst: gstForm.taxGst,
      });

      existingRestaurantGstRef.current = updatedGst;

      setGstForm((prev) => ({
        ...prev,
        gst: updatedGst,
      }));

      setFormData((prev) => ({
        ...prev,
        restaurantgst: updatedGst,
      }));

      setGstModalOpen(false);
    } catch (error) {
      console.error("updateGSTDetails failed:", error);
      toast.error("Failed to update GST details");
    } finally {
      setSavingGst(false);
    }
  };

  const validateChatbotMessage = (message = "") => {
    const trimmed = message.trim();

    if (!trimmed) {
      toast.error("Please enter chatbot message");
      return false;
    }

    if (!trimmed.includes("{0}")) {
      toast.error("Customer name placeholder {0} is mandatory");
      return false;
    }

    if (!trimmed.includes("{1}")) {
      toast.error("Business name placeholder {1} is mandatory");
      return false;
    }

    return true;
  };

  const handleSaveChatbotMessage = async () => {
    if (!client?.us_code) {
      toast.error("Client id missing");
      return;
    }

    if (!validateChatbotMessage(chatbotMessage)) return;

    setChatbotMessageLoading(true);

    try {
      await apiRef.current.insertClientCustomMessage({
        userid: client.us_code,
        customMessage: chatbotMessage.trim(),
      });
    } finally {
      setChatbotMessageLoading(false);
    }
  };

  const handleRefreshChatbotMessage = async () => {
    if (!client?.us_code) {
      toast.error("Client id missing");
      return;
    }

    setChatbotMessageLoading(true);

    try {
      const customMessage = await apiRef.current.getClientCustomMessage(
        client.us_code,
      );
      setChatbotMessage(customMessage || DEFAULT_CHATBOT_MESSAGE);
    } catch (error) {
      console.error("Failed to refresh chatbot message:", error);
      toast.error("Failed to refresh chatbot message");
    } finally {
      setChatbotMessageLoading(false);
    }
  };

  if (!client) {
    return (
      <div
        className={`flex h-[400px] items-center justify-center text-sm text-gray-500 ${THEME_BLUE_BG}`}
      >
        Initializing configuration module...
      </div>
    );
  }

  return (
    <>
      <main className="px-0">
        {initialLoading && !loadedOnce ? (
          <ConfigSkeleton />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
              <div className="xl:col-span-8">
                <ConfigFormCard
                  formData={formData}
                  updateForm={updateForm}
                  onSave={handleSave}
                  configLoading={initialLoading}
                  isSavingConfig={isSavingConfig}
                />
              </div>

              <div className="xl:col-span-4">
                <div className="space-y-5 xl:sticky xl:top-24">
                  <OrganizationImageCard
                    organization={organization}
                    clientName={client.organization_name || clientName}
                    preview={preview}
                    loading={
                      initialLoading || uploadingImage || savingOrganization
                    }
                    onEdit={() => setImageModalOpen(true)}
                  />

                  <GstDetailsCard
                    gstForm={gstForm}
                    halfTax={halfTax}
                    gstLoading={initialLoading}
                    onEdit={() => setGstModalOpen(true)}
                  />
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#01C37D]/10">
                    <MessageCircle size={15} className="text-[#01C37D]" />
                  </div>

                  <div className="min-w-0">
                    <h2 className="truncate text-xs font-bold text-gray-900">
                      Chatbot Welcome Message
                    </h2>
                    <p className="truncate text-[10px] text-gray-500">
                      {"{0}"} customer name and {"{1}"} business name are
                      mandatory.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleRefreshChatbotMessage}
                  disabled={chatbotMessageLoading}
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 text-[10px] font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw size={11} />
                  Refresh
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                <textarea
                  value={chatbotMessage}
                  onChange={(e) => setChatbotMessage(e.target.value)}
                  rows={6}
                  className="min-h-[135px] w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-4 text-gray-800 outline-none focus:border-[#01C37D] focus:bg-white"
                  placeholder="Enter chatbot message..."
                />

                <div className="min-h-[135px] rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="mb-1 text-[10px] font-bold text-gray-500">
                    Live Preview
                  </p>

                  <div className="max-h-[112px] overflow-y-auto whitespace-pre-wrap text-[11px] leading-4 text-gray-800">
                    {chatbotMessage
                      .replaceAll("{0}", "Kewal")
                      .replaceAll(
                        "{1}",
                        client.organization_name ||
                          clientName ||
                          "Your Business",
                      )}
                  </div>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-1.5 text-[9px]">
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700">
                    {"{0}"} = Customer
                  </span>

                  <span className="rounded-full bg-purple-50 px-2 py-0.5 font-semibold text-purple-700">
                    {"{1}"} = Business
                  </span>

                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                    Emojis unicode
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleSaveChatbotMessage}
                  disabled={
                    chatbotMessageLoading ||
                    busy?.[client?.us_code] === "chatbotMessage"
                  }
                  className="inline-flex h-7 items-center gap-1 rounded-md bg-[#01C37D] px-3 text-[10px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Save size={12} />
                  {chatbotMessageLoading ||
                  busy?.[client?.us_code] === "chatbotMessage"
                    ? "Saving..."
                    : "Save"}
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      <OrganizationImageModal
        open={imageModalOpen}
        preview={preview}
        organization={organization}
        form={organizationForm}
        uploading={uploadingImage}
        saving={savingOrganization}
        onClose={() => {
          if (uploadingImage || savingOrganization) return;
          setOrganizationForm(
            normalizeOrganizationDetails(organization || {}, {
              organizationName:
                organization?.organization_name ||
                client.organization_name ||
                clientName ||
                "",
              gst: gstForm.gst || "",
            }),
          );
          setPreview(null);
          setImageModalOpen(false);
        }}
        onChange={handleOrganizationFieldChange}
        onImageSelect={handleImageSelect}
        onSave={handleOrganizationSave}
      />

      <GstEditModal
        open={gstModalOpen}
        gstForm={gstForm}
        setGstForm={setGstForm}
        halfTax={halfTax}
        isSavingGst={isSavingGst}
        onClose={() => setGstModalOpen(false)}
        onSubmit={handleGstUpdate}
      />
    </>
  );
}